import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from '@vitness/icons';
import { cn } from '@vitness/theme';

/**
 * Side-anchored drawer built on Radix Dialog. Shares the Dialog accessibility
 * model (focus trap, scroll lock, Escape/overlay dismissal, focus return) but
 * slides in from a screen edge — used for detail panels, filters, and menus.
 *
 * Compose from: `Sheet` (root), `SheetTrigger`, `SheetContent` (with `side`),
 * `SheetHeader`, `SheetFooter`, `SheetTitle`, `SheetDescription`, `SheetClose`.
 */
export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;
export const SheetPortal = DialogPrimitive.Portal;

export type SheetProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Root>;
export type SheetSide = 'top' | 'right' | 'bottom' | 'left';

const SheetOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function SheetOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn('fixed inset-0 z-50 bg-black/55 backdrop-blur-sm', className)}
      {...props}
    />
  );
});

const SIDE_CLASSES: Record<SheetSide, string> = {
  top: 'inset-x-0 top-0 h-auto max-h-[85vh] w-full border-b',
  bottom: 'inset-x-0 bottom-0 h-auto max-h-[85vh] w-full border-t',
  left: 'inset-y-0 left-0 h-full w-3/4 max-w-sm border-r',
  right: 'inset-y-0 right-0 h-full w-3/4 max-w-sm border-l',
};

export interface SheetContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Screen edge the drawer anchors to. Defaults to `'right'`. */
  side?: SheetSide;
  /** Renders the built-in top-right close button. Defaults to `true`. */
  showClose?: boolean;
  children?: ReactNode;
}

/**
 * Drawer surface anchored to a screen edge. Focus is trapped while open and
 * returned to the trigger on close (Radix). The close button is keyboard
 * reachable and labelled.
 */
export const SheetContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(function SheetContent(
  { side = 'right', className, children, showClose = true, ...props },
  ref,
) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed z-50 flex flex-col gap-vn-4 p-vn-5',
          'border-border bg-surface-card text-text-primary shadow-vn-card font-body',
          'focus:outline-none',
          SIDE_CLASSES[side],
          className,
        )}
        {...props}
      >
        {children}
        {showClose ? (
          <DialogPrimitive.Close
            aria-label="Close"
            className={cn(
              'absolute right-vn-3 top-vn-3 inline-flex h-6 w-6 items-center justify-center',
              'rounded-vn-sm text-text-muted',
              'hover:bg-surface-card-hover hover:text-text-primary',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vn-border-strong)]',
              'transition-colors',
            )}
          >
            <X size={15} />
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </SheetPortal>
  );
});

export function SheetHeader({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return <div className={cn('flex flex-col gap-vn-1 pr-vn-6', className)} {...props} />;
}

export function SheetFooter({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      className={cn('mt-auto flex flex-col-reverse gap-vn-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

export const SheetTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function SheetTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        'font-display text-base font-semibold leading-tight text-text-primary',
        className,
      )}
      {...props}
    />
  );
});

export const SheetDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function SheetDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn('text-sm leading-relaxed text-text-secondary', className)}
      {...props}
    />
  );
});
